import React, { useState } from 'react';
import { get, cloneDeep } from 'lodash';
// checkbox component added to table.
const UNGROUPED = 'ungrouped';
const Checkbox = props => <input type="checkbox" {...props} />;

// used to toggle all rows in a group context.
export const toggleAll = ({ groupedFlatRows, state, groupContext, setSelectedState, areAllSelected }) => {
    const allSelected = areAllSelected({ groupedFlatRows, state, groupContext });
    const groupedRows = groupedFlatRows.filter(x => x.groupKey === groupContext);
    if (!groupedRows.length) {
        return false;
    }
    const groupKey = groupContext;
    // toggle everything based on if in grouped or ungrouped mode.
    const allRows = groupedRows.reduce((acc, x) => {
        acc[x.id] = !allSelected;
        return acc;
    }, {});

    if (groupKey === undefined) {
        setSelectedState(prev => ({
            ...prev,
            ...allRows
        }));
    } else {
        const newKey = groupKey === '' ? UNGROUPED : groupKey;
        setSelectedState(prev => ({
            ...prev,
            [newKey]: {
                ...prev[newKey],
                ...allRows
            }
        }));
    }
    return true;
};

// used to toggle selected on a single row.
export const toggleOne = ({ row, state, setSelectedState, isSelected }) => {
    const { groupKey } = row;
    const selected = isSelected({ row, state });
    if (groupKey === undefined) {
        // if no groupKey toggle the selectedRow state for this [row.id]
        setSelectedState(prev => ({
            ...prev,
            [row.id]: !selected
        }));
    } else {
        const newKey = groupKey === '' ? UNGROUPED : groupKey;
        // if gorup then toggle the selectedRow state for this [row.id] nested in groupKey object
        setSelectedState(prev => ({
            ...prev,
            [newKey]: {
                ...prev[newKey],
                [row.id]: !selected
            }
        }));
    }
};

const HooksFunction = (hooks) => {
    // used to track all of the currently selected rows
    const [selectedState, setSelectedState] = useState({});
    // this function is used to keep the internal table state in sync with external selectedState
    const useControlledState = state => React.useMemo(
        () => ({
            ...state,
            selectedRows: selectedState,
        }),
        [state, selectedState] // eslint-disable-line react-hooks/exhaustive-deps
    );
    // used to check if a row is currently selected
    const isSelected = ({ row, state }) => {
        const { groupKey, id } = row;
        const { selectedRows } = state;
        // if no group key just check for rowId in selectedRows object.
        if (groupKey === undefined) {
            return !!selectedRows[id];
        }
        const newKey = groupKey === '' ? UNGROUPED : groupKey;
        // if groupKey check selectedRows for group object then id in that object.
        return !!get(selectedRows, [newKey, id], null);
    };
    // used to check if all rows in a group context are selected.
    const areAllSelected = ({ groupedFlatRows, state, groupContext }) => {
        const groupedRows = groupedFlatRows.filter(x => x.groupKey === groupContext);
        // if no rows for groupContext return false
        if (!groupedRows.length) {
            return false;
        }

        const groupKey = groupContext;
        // if no group check if every groupedRow is in selectedRows.
        if (groupKey === undefined) {
            return groupedRows.every(x => !!state.selectedRows[x.id]);
        }
        // if group key then check based on group and row ids.
        const newKey = groupKey === '' ? UNGROUPED : groupKey;
        return groupedRows.every(x => !!get(state.selectedRows, [newKey, x.id], false));
    };
    // put checkbox column as first row of table.
    hooks.visibleColumns.push(cs => [
        {
            id: 'selection',
            canHide: false,
            width: 40,
            disableFilters: true,
            disableGlobalFilter: true,
            skipExport: true,
            Header: table => (
                <div>
                    <Checkbox
                        onClick={() => toggleAll({ ...table, setSelectedState, areAllSelected })}
                        checked={areAllSelected(table)}
                    />
                </div>
            ),
            Cell: table => (
                <div>
                    <Checkbox
                        onClick={() => toggleOne({ ...table, setSelectedState, isSelected })}
                        checked={isSelected(table)}
                    />
                </div>
            ),
        },
        ...cs,
    ]);
    // assigned the above to the useControlledState hook
    hooks.useControlledState.push(useControlledState);
    // this function is used to augment the table instance with some additional helpful group methods.
    function useInstance(instance) {
        const { groupedFlatRows, state } = instance;
        let selectedRows = [];
        if (state.groupBy.length) {
            selectedRows = groupedFlatRows.filter((x) => {
                const newKey = x.groupKey === '' ? UNGROUPED : x.groupKey;
                return get(state, ['selectedRows', newKey, x.id], false);
            });
        } else {
            selectedRows = groupedFlatRows.filter(x => get(state, ['selectedRows', x.id], false));
        }
        // selectedRows is all the selected rows across any grouped context
        instance.selectedRows = selectedRows;
        // get group selected rows is a function to search in the above selectedRows based on a group context.
        instance.getGroupSelectedRows = React.useCallback(
            groupId => selectedRows.filter(x => x.groupKey === groupId)
            , [selectedRows]);
        // remove all rows from selectedRows based on group context.
        instance.removeAllSelections = React.useCallback(
            () => setSelectedState({})
            , []);
        instance.removeGroupSelections = React.useCallback(
            groupId => setSelectedState((prev) => {
                const groupKey = groupId === '' ? UNGROUPED : groupId;
                const newObj = cloneDeep(prev);
                if (groupKey === undefined) {
                    return {};
                }
                delete newObj[groupKey];
                return newObj;
            })
            , []);
    }

    // assign the above useInstance to the table
    hooks.useInstance.push(useInstance);
    return null;
};
// returns empty hook function if row selection is not enabled.
const useRowGroupSelect = shouldEnableSelection => (shouldEnableSelection ? HooksFunction : () => null);
export default useRowGroupSelect;



// WEBPACK FOOTER //
// ./src/components/atoms/tables/smarttables/tableHooks/useRowGroupSelect.js